import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef, HostListener, ChangeDetectorRef } from '@angular/core';
import { Chart, ChartConfiguration, ChartType, registerables } from 'chart.js';
import { JobServicesService } from 'src/app/services/job-services.service';
import { GlobalServicesService } from 'src/app/services/global-services.service';
import { LoginService } from 'src/app/services/login.service';
import { Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import { switchMap, takeUntil, catchError } from 'rxjs/operators';

Chart.register(...registerables);

interface FunnelStage {
  name: string;
  value: number;
  fill: string;
}

interface TopRole {
  job_role_id: number;
  job_role_name: string;
  total_candidates: number;
  offers_accepted: number;
  offers_rejected: number;
  hire_rate: number;
  hire_rate_trend: number | null;
}

interface DropoffStage {
  name: string;
  entered: number;
  progressed: number;
  dropOff: number;
  conversionRate: number;
  fill: string;
}

interface EventTypeDistribution {
  event_type: string;
  total_candidates: number;
  event_count: number;
  percentage: number;
  fill: string;
}

interface InstitutionPerformance {
  institute_id: number;
  institute_name: string;
  total_registrations: number;
  total_offers: number;
  event_count: number;
  fill: string;
}

interface DailyActivity {
  date: string;
  registrations: number;
  interviewed: number;
  assessments_taken: number;
  offered: number;
}

interface SourcePerformance {
  name?: string;
  rejected?: number;
  accepted?: number;
  offered?: number;
  candidates?: number;
}

interface GenderBasedPerformance {
  gender?: string;
  candidates?: number;
  accepted?: number;
}

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.css'],
    standalone: false
})
export class DashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('funnelChartCanvas') funnelChartCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('dropoffChartCanvas') dropoffChartCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('topPerformingChartCanvas') topPerformingChartCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('eventTypeChartCanvas') eventTypeChartCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('campusStatsChartCanvas') campusStatsChartCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('dailyActivityChartCanvas') dailyActivityChartCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('sourcePerformanceChartCanvas') sourcePerformanceChartCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('genderBasedPerformanceChartCanvas') genderBasedPerformanceChartCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('chartsSectionRef') chartsSectionRef!: ElementRef;
  @ViewChild('funnelSectionRef') funnelSectionRef!: ElementRef;
  @ViewChild('analyticsSectionRef') analyticsSectionRef!: ElementRef;

  timeFilter = '30';
  showTimeFilter = false;
  funnelChartType: string = 'doughnut';
  rolesChartType: string = 'horizontal';
  dropoffChartType = 'horizontal';
  eventTypeChartType = 'doughnut';
  institutionChartType: string = 'horizontal';
  dailyActivityChartType: string = 'area';
  sourcePerformanceChartType: string = 'bar';
  genderBasedPerformanceChartType: string = 'bar';
  isSummaryLoading = true;
  isChartsLoading = true;
  isAnalyticsLoading = true;
  isFunnelLoading = true;
  userName: string = 'User';

  summaryData: any = null;
  funnelStages: FunnelStage[] = [];
  funnelData: any = null;
  dropoffData: DropoffStage[] = [];
  topPerformingData: TopRole[] = [];
  eventTypeData: EventTypeDistribution[] = [];
  institutionData: InstitutionPerformance[] = [];
  dailyActivityData: DailyActivity[] = [];
  sourcePerformanceData: SourcePerformance[] = []
  genderBasedPerformanceData: GenderBasedPerformance[] = []

  funnelChart: Chart | null = null;
  dropoffChart: Chart | null = null;
  rolesChart: Chart | null = null;
  eventTypeChart: Chart | null = null;
  institutionChart: Chart | null = null;
  dailyActivityChart: Chart | null = null;
  sourcePerformanceChart: Chart | null = null;
  genderBasedPerformanceChart: Chart | null = null;
  private resizeTimeout: any = null;
  private destroy$ = new Subject<void>();
  private timeFilter$ = new Subject<number>();
  private chartsRange$ = new Subject<number>();
  private funnelRange$ = new Subject<number>();
  private analyticsRange$ = new Subject<number>();
  private sectionVisible = { charts: false, funnel: false, analytics: false };
  private chartsObserver: IntersectionObserver | null = null;
  private funnelObserver: IntersectionObserver | null = null;
  private analyticsObserver: IntersectionObserver | null = null;

  timeOptions = [
    { value: '7', label: 'Last 7 days' },
    { value: '30', label: 'Last 30 days' },
    { value: '90', label: 'Last 90 days' },
    { value: '180', label: 'Last 180 days' },
    { value: '365', label: 'Last 365 days' }
  ];

  // Chart selector visibility
  showChartSelector: { [key: string]: boolean } = {
    dailyActivity: false,
    topRoles: false,
    funnel: false,
    dropoff: false,
    campusStats: false,
    eventType: false,
    sourcePerformance: false,
    genderBasedPerformance: false
  };

  chartTypeOptions = [
    { value: 'pyramid', label: 'Pyramid', icon: 'pi-sort-amount-down-alt' },
    { value: 'bar', label: 'Bar', icon: 'pi-chart-bar' },
    { value: 'pie', label: 'Pie', icon: 'pi-chart-pie' },
    { value: 'doughnut', label: 'Doughnut', icon: 'pi-circle-fill' },
    { value: 'radar', label: 'Radar', icon: 'pi-compass' },
    { value: 'horizontal', label: 'Horizontal Bar', icon: 'pi-bars' }
  ];

  dropoffChartOptions = [
    { value: 'funnel', label: 'Funnel', icon: 'pi-filter' },
    { value: 'horizontal', label: 'Horizontal Stacked', icon: 'pi-bars' },
    { value: 'bar', label: 'Vertical Bar', icon: 'pi-chart-bar' }
  ];

  rolesChartOptions = [
    { value: 'bar', label: 'Bar', icon: 'pi-chart-bar' },
    { value: 'horizontal', label: 'Horizontal Bar', icon: 'pi-bars' },
    { value: 'radar', label: 'Radar', icon: 'pi-compass' },
    { value: 'polarArea', label: 'Polar Area', icon: 'pi-sun' },
    { value: 'doughnut', label: 'Doughnut', icon: 'pi-circle-fill' }
  ];

  eventTypeChartOptions = [
    { value: 'doughnut', label: 'Doughnut', icon: 'pi-circle-fill' },
    { value: 'pie', label: 'Pie', icon: 'pi-chart-pie' },
    { value: 'polarArea', label: 'Polar Area', icon: 'pi-sun' },
    { value: 'bar', label: 'Bar', icon: 'pi-chart-bar' }
  ];

  institutionChartOptions = [
    { value: 'bar', label: 'Bar', icon: 'pi-chart-bar' },
    { value: 'horizontal', label: 'Horizontal Bar', icon: 'pi-bars' },
    { value: 'doughnut', label: 'Doughnut', icon: 'pi-circle-fill' },
    { value: 'table', label: 'Table', icon: 'pi-table' },
    { value: 'polarArea', label: 'Polar Area', icon: 'pi-sun' }
  ];

  dailyActivityChartOptions = [
    { value: 'area', label: 'Area', icon: 'pi-chart-line' },
    { value: 'bar', label: 'Bar', icon: 'pi-chart-bar' },
    { value: 'radar', label: 'Radar', icon: 'pi-compass' }
  ];

  sourcePerformanceChartOptions = [
    { value: 'bar', label: 'Bar', icon: 'pi-chart-bar' },
    { value: 'pie', label: 'Pie', icon: 'pi-chart-pie' },
    { value: 'doughnut', label: 'Doughnut', icon: 'pi-circle-fill' },
    { value: 'area', label: 'Area', icon: 'pi-chart-line' },
  ]

  genderBasedPerformanceChartOptions = [
    { value: 'bar', label: 'Bar', icon: 'pi-chart-bar' },
    { value: 'pie', label: 'Pie', icon: 'pi-chart-pie' },
    { value: 'doughnut', label: 'Doughnut', icon: 'pi-circle-fill' },
    { value: 'area', label: 'Area', icon: 'pi-chart-line' },
  ]

  userMeta$ = this.globalService.userMeta$;

  constructor(
    private jobService: JobServicesService,
    private globalService: GlobalServicesService,
    private loginService: LoginService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) { }

  getUserInitials(userProfile: any): string {
    if (userProfile?.first_name && userProfile?.last_name) {
      return (userProfile.first_name.charAt(0) + userProfile.last_name.charAt(0)).toUpperCase();
    }
    return 'AD';
  }

  getFullName(userProfile: any): string {
    if (userProfile?.first_name && userProfile?.last_name) {
      return `${userProfile.first_name} ${userProfile.last_name}`;
    }
    return 'Admin';
  }

  viewProfile(): void {
    this.router.navigate(['/settings'], {
      queryParams: { menu: 'account' }
    });
  }

  signOut(): void {
    for (let attr in localStorage) {
      localStorage.removeItem(attr);
    }
    this.loginService.validToken.next({
      status: 403,
      statusMessage: 'loggedOut',
    });
    this.router.navigate(['/logout']);
  }

  ngOnInit(): void {
    this.getUserName();

    this.timeFilter$.pipe(
      switchMap(range => this.jobService.getDashboardSummary(range).pipe(catchError(() => of(null)))),
      takeUntil(this.destroy$)
    ).subscribe((response: any) => {
      if (!response) {
        this.summaryData = null;
        this.funnelData = null;
        this.funnelStages = [];
        this.isSummaryLoading = false;
        return;
      }
      this.summaryData = response.events || {};
      this.funnelData = response.funnel || {};
      this.processFunnelData(this.funnelData);
      this.isSummaryLoading = false;
      this.cdr.detectChanges();
      requestAnimationFrame(() => setTimeout(() => this.renderFunnelChart(), 100));
    });

    this.chartsRange$.pipe(
      switchMap(range => this.jobService.getDashboardCharts(range).pipe(catchError(() => of(null)))),
      takeUntil(this.destroy$)
    ).subscribe((response: any) => {
      if (!response) {
        this.topPerformingData = [];
        this.eventTypeData = [];
        this.institutionData = [];
        this.dailyActivityData = [];
        this.isChartsLoading = false;
        return;
      }
      this.topPerformingData = response.top_jobs || [];
      this.eventTypeData = this.processEventTypeData(response.event_type_distribution || []);
      this.institutionData = this.processInstitutionData(response.institution_performance || []);
      this.dailyActivityData = response.daily_activity || [];
      this.isChartsLoading = false;
      this.cdr.detectChanges();
      requestAnimationFrame(() => setTimeout(() => {
        this.renderDailyActivityChart();
        this.renderRolesChart();
        this.renderInstitutionChart();
        this.renderEventTypeChart();
      }, 100));
    });

    this.analyticsRange$.pipe(
      switchMap(range => this.jobService.getDashboardAnalytics(range).pipe(catchError(() => of(null)))),
      takeUntil(this.destroy$)
    ).subscribe((response: any) => {
      if (!response) {
        this.sourcePerformanceData = [];
        this.genderBasedPerformanceData = [];
        this.isAnalyticsLoading = false;
        return;
      }
      this.sourcePerformanceData = response.source_performance || [];
      this.genderBasedPerformanceData = response.gender_based_performance || [];
      this.isAnalyticsLoading = false;
      this.cdr.detectChanges();
      requestAnimationFrame(() => setTimeout(() => {
        this.renderSourcePerformanceChart();
        this.renderGenderBasedPerformanceChart();
      }, 100));
    });

    this.funnelRange$.pipe(
      switchMap(range => this.jobService.getDashboardRecruitmentFunnel(range).pipe(catchError(() => of(null)))),
      takeUntil(this.destroy$)
    ).subscribe((response: any) => {
      if (!response) {
        this.dropoffData = [];
        this.isFunnelLoading = false;
        return;
      }
      this.dropoffData = this.processDropoffData(response.recruitment_funnel || {});
      this.isFunnelLoading = false;
      this.cdr.detectChanges();
      requestAnimationFrame(() => setTimeout(() => this.renderDropoffChart(), 100));
    });

    this.loadData();
  }

  ngAfterViewInit(): void {
    const options: IntersectionObserverInit = { threshold: 0.05 };

    this.chartsObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !this.sectionVisible.charts) {
        this.sectionVisible.charts = true;
        this.isChartsLoading = true;
        this.chartsRange$.next(Number(this.timeFilter));
      }
    }, options);
    this.chartsObserver.observe(this.chartsSectionRef.nativeElement);

    this.funnelObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !this.sectionVisible.funnel) {
        this.sectionVisible.funnel = true;
        this.isFunnelLoading = true;
        this.funnelRange$.next(Number(this.timeFilter));
      }
    }, options);
    this.funnelObserver.observe(this.funnelSectionRef.nativeElement);

    this.analyticsObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        if (!this.sectionVisible.charts) {
          this.sectionVisible.charts = true;
          this.isChartsLoading = true;
          this.chartsRange$.next(Number(this.timeFilter));
        }
        if (!this.sectionVisible.analytics) {
          this.sectionVisible.analytics = true;
          this.isAnalyticsLoading = true;
          this.analyticsRange$.next(Number(this.timeFilter));
        }
      }
    }, options);
    this.analyticsObserver.observe(this.analyticsSectionRef.nativeElement);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.resizeTimeout = setTimeout(() => {
      if (this.funnelChart) this.funnelChart.resize();
      if (this.dropoffChart) this.dropoffChart.resize();
      if (this.rolesChart) this.rolesChart.resize();
      if (this.eventTypeChart) this.eventTypeChart.resize();
      if (this.institutionChart) this.institutionChart.resize();
      if (this.dailyActivityChart) this.dailyActivityChart.resize();
      if (this.sourcePerformanceChart) this.sourcePerformanceChart.resize();
      if (this.genderBasedPerformanceChart) this.genderBasedPerformanceChart.resize();
    }, 250);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;

    // Check if click is outside chart selector elements
    const isClickInside = target.closest('.chart-selector-trigger') ||
      target.closest('.chart-selector-panel');

    if (!isClickInside) {
      this.closeAllChartSelectors();
    }

    // Check if click is outside time filter dropdown
    const isTimeFilterClick = target.closest('.modern-dropdown');
    if (!isTimeFilterClick) {
      this.showTimeFilter = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.chartsObserver?.disconnect();
    this.funnelObserver?.disconnect();
    this.analyticsObserver?.disconnect();

    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = null;
    }

    if (this.funnelChart) {
      this.funnelChart.destroy();
      this.funnelChart = null;
    }
    if (this.dropoffChart) {
      this.dropoffChart.destroy();
      this.dropoffChart = null;
    }
    if (this.rolesChart) {
      this.rolesChart.destroy();
      this.rolesChart = null;
    }
    if (this.eventTypeChart) {
      this.eventTypeChart.destroy();
      this.eventTypeChart = null;
    }
    if (this.institutionChart) {
      this.institutionChart.destroy();
      this.institutionChart = null;
    }
    if (this.dailyActivityChart) {
      this.dailyActivityChart.destroy();
      this.dailyActivityChart = null;
    }
    if (this.genderBasedPerformanceChart) {
      this.genderBasedPerformanceChart.destroy();
      this.genderBasedPerformanceChart = null;
    }
    if (this.sourcePerformanceChart) {
      this.sourcePerformanceChart.destroy();
      this.sourcePerformanceChart = null;
    }
  }

  getUserName(): void {
    this.globalService.userMeta$.subscribe(meta => {
      const userProfile = meta?.user_profile;
      if (userProfile?.first_name) {
        this.userName = userProfile.first_name;
      } else {
        this.userName = 'User';
      }
    });
  }

  loadData(): void {
    const range = Number(this.timeFilter);
    this.isSummaryLoading = true;
    this.timeFilter$.next(range);

    if (this.sectionVisible.charts) {
      this.isChartsLoading = true;
      this.chartsRange$.next(range);
    }
    if (this.sectionVisible.funnel) {
      this.isFunnelLoading = true;
      this.funnelRange$.next(range);
    }
    if (this.sectionVisible.analytics) {
      this.isAnalyticsLoading = true;
      this.analyticsRange$.next(range);
    }
  }

  processFunnelData(data: any): void {
    // Safely extract values with proper fallbacks
    const invited = data.invited || 0;
    const applied = data.applied || 0;
    const eligible = data.eligible?.eligible || 0;
    const shortlisted = data.eligible?.shortlisted || 0;
    const offered = data.offers?.offered || 0;
    const accepted = data.offers?.accepted || 0;

    this.funnelStages = [
      { name: 'Invited', value: invited, fill: '#6366F1' },
      { name: 'Applied', value: applied, fill: '#8B5CF6' },
      { name: 'Eligible', value: eligible, fill: '#EC4899' },
      { name: 'Shortlisted', value: shortlisted, fill: '#F59E0B' },
      { name: 'Offered', value: offered, fill: '#10B981' },
      { name: 'Accepted', value: accepted, fill: '#14B8A6' }
    ].filter(stage => stage.value > 0); // Filter out zero values for cleaner display
  }

  processDropoffData(recruitmentFunnel: any): DropoffStage[] {
    const stageMetrics = recruitmentFunnel.stageTypeMetrics || [];

    const stageColors: { [key: string]: string } = {
      'Eligibility Check': '#6366F1',
      'Screening': '#8B5CF6',
      'Test': '#A78BFA',
      'Interview': '#EC4899',
      'Offer': '#10B981',
      'Onboarding': '#14B8A6'
    };

    return stageMetrics
      .filter((stage: any) => stage.entered > 0)  // Only show stages with candidates
      .map((stage: any) => ({
        name: stage.stageType,
        entered: stage.entered || 0,
        progressed: stage.progressed || 0,
        dropOff: stage.dropOff || 0,
        conversionRate: stage.conversionRate || 0,
        fill: stageColors[stage.stageType] || '#6B7280'
      }));
  }

  processEventTypeData(eventTypeDistribution: any[]): EventTypeDistribution[] {
    const eventTypeColors = [
      '#6366F1', // Indigo
      '#8B5CF6', // Purple
      '#EC4899', // Pink
      '#10B981', // Emerald
      '#F59E0B'  // Amber
    ];

    return eventTypeDistribution.slice(0, 5).map((eventType: any, index: number) => ({
      event_type: eventType.event_type,
      total_candidates: eventType.total_candidates || 0,
      event_count: eventType.event_count || 0,
      percentage: eventType.percentage || 0,
      fill: eventTypeColors[index % eventTypeColors.length]
    }));
  }

  processInstitutionData(institutionPerformance: any[]): InstitutionPerformance[] {
    const institutionColors = [
      '#6366F1', '#8B5CF6', '#EC4899', '#10B981', '#F59E0B'
    ];

    return institutionPerformance.slice(0, 10).map((inst: any, index: number) => ({
      institute_id: inst.institute_id,
      institute_name: inst.institute_name,
      total_registrations: inst.total_registrations || 0,
      total_offers: inst.total_offers || 0,
      event_count: inst.event_count || 0,
      fill: institutionColors[index % institutionColors.length]
    }));
  }

  onFunnelChartTypeChange(): void {
    this.renderFunnelChart();
  }

  onDropoffChartTypeChange(): void {
    this.renderDropoffChart();
  }

  onRolesChartTypeChange(): void {
    this.renderRolesChart();
  }

  onEventTypeChartTypeChange(): void {
    this.renderEventTypeChart();
  }

  onInstitutionChartTypeChange(): void {
    this.renderInstitutionChart();
  }

  onDailyActivityChartTypeChange(): void {
    this.renderDailyActivityChart();
  }

  renderAllCharts(): void {
    // Use requestAnimationFrame and setTimeout to ensure DOM is ready
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.cdr.detectChanges();
        setTimeout(() => {
          this.renderFunnelChart();
          this.renderDropoffChart();
          this.renderRolesChart();
          this.renderEventTypeChart();
          this.renderInstitutionChart();
          this.renderDailyActivityChart();
          this.renderSourcePerformanceChart();
          this.renderGenderBasedPerformanceChart();
        }, 100);
      }, 50);
    });
  }

  renderFunnelChart(): void {
    if (this.funnelChart) {
      this.funnelChart.destroy();
      this.funnelChart = null;
    }

    if (this.isSummaryLoading || !this.funnelStages || this.funnelStages.length === 0) return;

    // Wait for view to update if canvas doesn't exist yet
    if (!this.funnelChartCanvas) {
      this.cdr.detectChanges();
      setTimeout(() => this.renderFunnelChart(), 100);
      return;
    }

    const canvas = this.funnelChartCanvas.nativeElement;
    if (!canvas) {
      setTimeout(() => this.renderFunnelChart(), 100);
      return;
    }

    const existingChart = Chart.getChart(canvas);
    if (existingChart) {
      existingChart.destroy();
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setTimeout(() => this.renderFunnelChart(), 100);
      return;
    }

    setTimeout(() => {
      if (!Chart.getChart(canvas)) {
        const config = this.getChartConfig(this.funnelChartType, this.funnelStages);
        this.funnelChart = new Chart(ctx, config);
      }
    }, 50);
  }

  renderDropoffChart(): void {
    if (this.dropoffChart) {
      this.dropoffChart.destroy();
      this.dropoffChart = null;
    }

    // Skip rendering if horizontal chart type (uses HTML instead of canvas)
    if (this.dropoffChartType === 'horizontal') {
      return;
    }

    if (this.isFunnelLoading || !this.dropoffData || this.dropoffData.length === 0) return;

    // Wait for view to update if canvas doesn't exist yet
    if (!this.dropoffChartCanvas) {
      this.cdr.detectChanges();
      setTimeout(() => this.renderDropoffChart(), 100);
      return;
    }

    const canvas = this.dropoffChartCanvas.nativeElement;
    if (!canvas) {
      setTimeout(() => this.renderDropoffChart(), 100);
      return;
    }

    const existingChart = Chart.getChart(canvas);
    if (existingChart) {
      existingChart.destroy();
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setTimeout(() => this.renderDropoffChart(), 100);
      return;
    }

    setTimeout(() => {
      if (!Chart.getChart(canvas)) {
        const labels = this.dropoffData.map(d => d.name);
        const enteredData = this.dropoffData.map(d => d.entered);
        const progressedData = this.dropoffData.map(d => d.progressed);
        const dropoffData = this.dropoffData.map(d => d.dropOff);

        const isFunnel = this.dropoffChartType === 'funnel';

        const config: ChartConfiguration = {
          type: 'bar' as ChartType,
          data: {
            labels: labels,
            datasets: isFunnel ? [
              {
                label: 'Candidates',
                data: enteredData,
                backgroundColor: enteredData.map((val, idx) => {
                  const opacity = 0.3 + (0.7 * (enteredData.length - idx) / enteredData.length);
                  return `rgba(99, 102, 241, ${opacity})`;
                }),
                borderColor: '#6366F1',
                borderWidth: 2,
                borderRadius: 6,
                barPercentage: 0.8
              }
            ] : [
              {
                label: 'Progressed',
                data: progressedData,
                backgroundColor: '#6366F1',
                borderRadius: 4,
                borderSkipped: false,
                barPercentage: this.dropoffChartType === 'bar' ? 0.6 : 0.7,
                ...(this.dropoffChartType === 'bar' ? {
                  barThickness: 40,
                  maxBarThickness: 50
                } : {})
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: (this.dropoffChartType === 'horizontal' || isFunnel) ? 'y' as const : 'x' as const,
            interaction: {
              mode: 'index' as const,
              intersect: false
            },
            layout: {
              padding: {
                right: 40 // Add space for dropoff labels
              }
            },
            plugins: {
              legend: {
                display: !isFunnel,
                position: 'bottom' as const,
                labels: {
                  font: { size: 11, family: 'Poppins, sans-serif' },
                  color: '#6B7280',
                  padding: 15,
                  usePointStyle: true,
                  pointStyle: 'circle'
                }
              },
              tooltip: {
                enabled: true,
                backgroundColor: '#fff',
                titleColor: '#111827',
                bodyColor: '#6b7280',
                borderColor: '#e5e7eb',
                borderWidth: 1,
                padding: 14,
                boxPadding: 8,
                usePointStyle: true,
                displayColors: false,
                titleFont: { size: 13, family: 'Poppins, sans-serif', weight: 'bold' as const },
                bodyFont: { size: 12, family: 'Poppins, sans-serif' },
                boxWidth: 8,
                boxHeight: 8,
                callbacks: {
                  title: (context: any) => {
                    return context[0].label;
                  },
                  label: (context: any) => {
                    const label = context.dataset.label || '';
                    return `${label}: ${context.parsed.x || context.parsed.y}`;
                  },
                  afterBody: (context: any) => {
                    const index = context[0].dataIndex;
                    const stage = this.dropoffData[index];
                    return isFunnel ? [
                      `Progressed: ${stage.progressed}`,
                      `Drop-off: ${stage.dropOff} ↓`,
                      `Conversion: ${(stage.conversionRate * 100).toFixed(0)}%`
                    ] : [
                      `Total Entered: ${stage.entered}`,
                      `Drop-off: ${stage.dropOff} ↓`,
                      `Conversion: ${(stage.conversionRate * 100).toFixed(0)}%`
                    ];
                  },
                  labelColor: () => ({
                    borderColor: '#8b5cf6',
                    backgroundColor: '#8b5cf6'
                  })
                }
              }
            },
            scales: {
              [isFunnel || this.dropoffChartType === 'horizontal' ? 'x' : 'y']: {
                stacked: !isFunnel,
                beginAtZero: true,
                grid: {
                  color: '#F3F4F6',
                  display: true
                },
                border: {
                  display: false
                },
                ticks: {
                  color: '#6B7280',
                  font: { size: 11, family: 'Poppins, sans-serif' }
                }
              },
              [isFunnel || this.dropoffChartType === 'horizontal' ? 'y' : 'x']: {
                stacked: !isFunnel,
                grid: {
                  display: false
                },
                border: {
                  display: false
                },
                ticks: {
                  color: '#374151',
                  font: { size: 11, family: 'Poppins, sans-serif' },
                  padding: this.dropoffChartType === 'bar' ? 12 : 8
                }
              }
            }
          },
          plugins: [{
            id: 'dropoffLabels',
            afterDatasetsDraw: (chart: any) => {
              const ctx = chart.ctx;
              const meta = chart.getDatasetMeta(0);

              meta.data.forEach((bar: any, index: number) => {
                const dropoff = dropoffData[index];
                if (dropoff > 0) {
                  ctx.save();
                  ctx.font = 'bold 11px Poppins, sans-serif';
                  ctx.fillStyle = '#EF4444';

                  const text = `↓ ${dropoff}`;

                  // Position at bar end with minimal gap (2px)
                  if (isFunnel || this.dropoffChartType === 'horizontal') {
                    // Horizontal/Funnel: bar.x is the end of the bar
                    const barEndX = bar.x;
                    const finalX = barEndX + 2;

                    ctx.textAlign = 'left';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(text, finalX, bar.y);
                  } else {
                    // Vertical: bar.x is center, width is thickness
                    const barRightX = bar.x + (bar.width / 2);
                    const barTopY = bar.y;
                    const finalX = barRightX + 2;

                    ctx.textAlign = 'left';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(text, finalX, barTopY);
                  }

                  ctx.restore();
                }
              });
            }
          }]
        };

        this.dropoffChart = new Chart(ctx, config);
      }
    }, 50);
  }

  renderRolesChart(): void {
    if (this.rolesChart) {
      this.rolesChart.destroy();
      this.rolesChart = null;
    }

    if (!this.topPerformingChartCanvas || this.isChartsLoading) return;

    const canvas = this.topPerformingChartCanvas.nativeElement;
    const existingChart = Chart.getChart(canvas);
    if (existingChart) {
      existingChart.destroy();
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    setTimeout(() => {
      if (!Chart.getChart(canvas)) {
        const topRoles = this.topPerformingData.slice(0, 5);
        const labels = topRoles.map(r => this.truncateText(r.job_role_name, 15));
        const candidateData = topRoles.map(r => r.total_candidates);
        const colors = ['#8B5CF6', '#6366F1', '#EC4899', '#10B981', '#F59E0B'];

        const isCircular = ['doughnut', 'pie', 'polarArea', 'radar'].includes(this.rolesChartType);
        const isArea = this.rolesChartType === 'area';

        let chartType: ChartType = 'bar';
        if (this.rolesChartType === 'doughnut') chartType = 'doughnut';
        else if (this.rolesChartType === 'polarArea') chartType = 'polarArea';
        else if (this.rolesChartType === 'radar') chartType = 'radar';
        else if (isArea) chartType = 'line';
        else chartType = 'bar';

        const config: ChartConfiguration = {
          type: chartType,
          data: {
            labels: labels,
            datasets: isCircular ? [{
              label: 'Total Candidates',
              data: candidateData,
              backgroundColor: colors,
              borderWidth: 0
            }] : [{
              label: 'Total Candidates',
              data: candidateData,
              backgroundColor: isArea ? this.createGradient(ctx, '#8B5CF6', true) : '#8B5CF6',
              borderColor: '#8B5CF6',
              borderWidth: isArea ? 3 : 0,
              borderRadius: this.rolesChartType === 'bar' || this.rolesChartType === 'horizontal' ? 6 : 0,
              tension: 0.4,
              pointRadius: isArea ? 0 : 0,
              pointHoverRadius: isArea ? 6 : 0,
              pointBackgroundColor: '#8B5CF6',
              pointBorderColor: '#fff',
              pointBorderWidth: 2,
              fill: isArea
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: this.rolesChartType === 'horizontal' ? 'y' as const : 'x' as const,
            interaction: {
              mode: 'index' as const,
              intersect: false
            },
            plugins: {
              legend: {
                display: isCircular,
                position: 'bottom' as const,
                labels: {
                  font: { size: 11, family: 'Poppins, sans-serif' },
                  color: '#6B7280',
                  padding: 15,
                  usePointStyle: true,
                  pointStyle: 'circle'
                }
              },
              tooltip: {
                enabled: true,
                backgroundColor: '#fff',
                titleColor: '#111827',
                bodyColor: '#6b7280',
                borderColor: '#e5e7eb',
                borderWidth: 1,
                padding: 14,
                boxPadding: 8,
                usePointStyle: true,
                displayColors: false,
                titleFont: { size: 13, family: 'Poppins, sans-serif', weight: 'bold' as const },
                bodyFont: { size: 12, family: 'Poppins, sans-serif', lineHeight: 1.8 },
                boxWidth: 8,
                boxHeight: 8,
                callbacks: {
                  title: (context: any) => {
                    const index = context[0].dataIndex;
                    return topRoles[index].job_role_name;
                  },
                  label: (context: any) => {
                    let value: number = 0;
                    if (typeof context.parsed === 'number') {
                      value = context.parsed;
                    } else if (context.parsed && typeof context.parsed === 'object') {
                      if (this.rolesChartType === 'horizontal') {
                        value = context.parsed.x !== undefined ? context.parsed.x : (context.raw || 0);
                      } else {
                        value = context.parsed.y || context.parsed.x || context.parsed.r || (context.raw || 0);
                      }
                    } else {
                      value = context.raw || 0;
                    }
                    return `Total Candidates: ${value}`;
                  },
                  afterBody: (context: any) => {
                    const index = context[0].dataIndex;
                    const role = topRoles[index];
                    return [
                      `Offers Accepted: ${role.offers_accepted}`,
                      `Offers Rejected: ${role.offers_rejected}`,
                      `Hire Rate: ${role.hire_rate.toFixed(1)}%`
                    ];
                  },
                  labelColor: (context: any) => {
                    return {
                      borderColor: '#8b5cf6',
                      backgroundColor: '#8b5cf6'
                    };
                  }
                }
              }
            },
            scales: isCircular ? undefined : {
              [this.rolesChartType === 'horizontal' ? 'x' : 'y']: {
                beginAtZero: true,
                grid: {
                  color: '#F3F4F6',
                  display: true
                },
                border: {
                  display: false
                },
                ticks: {
                  color: '#6B7280',
                  font: { size: 11, family: 'Poppins, sans-serif' }
                }
              },
              [this.rolesChartType === 'horizontal' ? 'y' : 'x']: {
                grid: {
                  display: false
                },
                border: {
                  display: false
                },
                ticks: {
                  color: '#374151',
                  font: { size: 11, family: 'Poppins, sans-serif' },
                  padding: 8
                }
              }
            }
          }
        };

        this.rolesChart = new Chart(ctx, config);
      }
    }, 50);
  }

  truncateText(text: string, maxLength: number): string {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }

  renderEventTypeChart(): void {
    if (this.eventTypeChart) {
      this.eventTypeChart.destroy();
      this.eventTypeChart = null;
    }

    if (!this.eventTypeChartCanvas || this.isChartsLoading || !this.eventTypeData || this.eventTypeData.length === 0) return;

    const canvas = this.eventTypeChartCanvas.nativeElement;
    const existingChart = Chart.getChart(canvas);
    if (existingChart) {
      existingChart.destroy();
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    setTimeout(() => {
      if (!Chart.getChart(canvas)) {
        const labels = this.eventTypeData.map(d => d.event_type);
        const candidateData = this.eventTypeData.map(d => d.total_candidates);
        const eventCountData = this.eventTypeData.map(d => d.event_count);
        const colors = this.eventTypeData.map(d => d.fill);

        // For pie/doughnut charts, use event_count data and filter out zero values
        const isPieDoughnut = this.eventTypeChartType === 'doughnut' || this.eventTypeChartType === 'pie' || this.eventTypeChartType === 'polarArea';
        const pieDoughnutData = isPieDoughnut
          ? this.eventTypeData
              .map((d, idx) => ({ label: d.event_type, value: d.event_count || 0, color: colors[idx], originalData: d }))
              .filter(item => item.value > 0)
          : [];

        // If pie/doughnut but no valid data, don't render
        if (isPieDoughnut && pieDoughnutData.length === 0) {
          return;
        }

        const config: ChartConfiguration = {
          type: isPieDoughnut
            ? this.eventTypeChartType as ChartType
            : 'bar' as ChartType,
          data: {
            labels: isPieDoughnut
              ? pieDoughnutData.map(d => d.label)
              : labels,
            datasets: isPieDoughnut
              ? [{
                label: 'Event Count',
                data: pieDoughnutData.map(d => d.value),
                backgroundColor: pieDoughnutData.map(d => d.color),
                borderWidth: 0
              }]
              : [
                {
                  label: 'Total Candidates',
                  data: candidateData,
                  backgroundColor: '#6366F1',
                  borderRadius: 6,
                  borderSkipped: false
                },
                {
                  label: 'Event Count',
                  data: eventCountData,
                  backgroundColor: '#10B981',
                  borderRadius: 6,
                  borderSkipped: false
                }
              ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
              mode: 'index' as const,
              intersect: false
            },
            plugins: {
              legend: {
                display: true,
                position: 'bottom' as const,
                labels: {
                  font: { size: 11, family: 'Poppins, sans-serif' },
                  color: '#6B7280',
                  padding: 12,
                  usePointStyle: true
                }
              },
              tooltip: {
                enabled: true,
                backgroundColor: '#fff',
                titleColor: '#111827',
                bodyColor: '#6b7280',
                borderColor: '#e5e7eb',
                borderWidth: 1,
                padding: 12,
                boxPadding: 6,
                usePointStyle: true,
                displayColors: false,
                titleFont: { size: 13, family: 'Poppins, sans-serif', weight: 'bold' as const },
                bodyFont: { size: 12, family: 'Poppins, sans-serif', lineHeight: 1.8 },
                boxWidth: 8,
                boxHeight: 8,
                callbacks: {
                  label: (context: any) => {
                    const label = context.dataset.label || '';
                    let value: number = 0;
                    if (typeof context.parsed === 'number') value = context.parsed;
                    else if (context.parsed && typeof context.parsed === 'object') {
                      value = context.parsed.y || context.parsed.x || context.parsed.r || 0;
                    } else value = context.raw || 0;
                    return `${label}: ${value}`;
                  },
                  afterBody: (context: any) => {
                    if (isPieDoughnut) {
                      const index = context[0].dataIndex;
                      if (pieDoughnutData[index]) {
                        const originalData = pieDoughnutData[index].originalData;
                        return [
                          `Candidates: ${originalData.total_candidates || 0}`,
                          `Percentage: ${originalData.percentage.toFixed(1)}%`
                        ];
                      }
                      return [];
                    } else {
                    const index = context[0].dataIndex;
                    const item = this.eventTypeData[index];
                    return [
                      `Percentage: ${item.percentage.toFixed(1)}%`
                    ];
                    }
                  },
                  labelColor: (context: any) => {
                    return {
                      borderColor: '#8b5cf6',
                      backgroundColor: '#8b5cf6'
                    };
                  }
                }
              }
            },
            scales: this.eventTypeChartType === 'doughnut' || this.eventTypeChartType === 'pie' || this.eventTypeChartType === 'polarArea' ? undefined : {
              y: {
                beginAtZero: true,
                grid: {
                  color: '#F3F4F6',
                  display: true
                },
                border: {
                  display: false
                },
                ticks: {
                  color: '#6B7280',
                  font: { size: 11, family: 'Poppins, sans-serif' }
                }
              },
              x: {
                grid: {
                  display: false
                },
                border: {
                  display: false
                },
                ticks: {
                  color: '#374151',
                  font: { size: 11, family: 'Poppins, sans-serif' }
                }
              }
            }
          }
        };

        this.eventTypeChart = new Chart(ctx, config);
      }
    }, 50);
  }

  renderInstitutionChart(): void {
    if (this.institutionChart) {
      this.institutionChart.destroy();
      this.institutionChart = null;
    }

    // Skip rendering if horizontal or table chart type (uses HTML instead of canvas)
    if (this.institutionChartType === 'horizontal' || this.institutionChartType === 'table') {
      return;
    }

    if (!this.campusStatsChartCanvas || this.isChartsLoading) return;

    const canvas = this.campusStatsChartCanvas.nativeElement;
    const existingChart = Chart.getChart(canvas);
    if (existingChart) {
      existingChart.destroy();
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    setTimeout(() => {
      if (!Chart.getChart(canvas)) {
        const labels = this.institutionData.map(d => this.getInstitutionAbbreviation(d.institute_name));
        const registrationData = this.institutionData.map(d => d.total_registrations);
        const offerData = this.institutionData.map(d => d.total_offers);
        const colors = ['#6366F1', '#8B5CF6', '#EC4899', '#10B981', '#F59E0B'];

        const isCircular = ['polarArea', 'pie', 'doughnut'].includes(this.institutionChartType);
        let chartType: ChartType = 'bar';
        if (this.institutionChartType === 'polarArea') chartType = 'polarArea';
        else if (this.institutionChartType === 'pie') chartType = 'pie';
        else if (this.institutionChartType === 'doughnut') chartType = 'doughnut';
        else chartType = 'bar';

        const config: ChartConfiguration = {
          type: chartType,
          data: {
            labels: labels,
            datasets: (this.institutionChartType === 'polarArea' || this.institutionChartType === 'pie' || this.institutionChartType === 'doughnut') ? [
              {
                label: 'Registrations',
                data: registrationData,
                backgroundColor: colors,
                borderWidth: this.institutionChartType === 'pie' || this.institutionChartType === 'doughnut' ? 2 : 0,
                borderColor: this.institutionChartType === 'pie' || this.institutionChartType === 'doughnut' ? '#fff' : undefined
              }
            ] : [
              {
                label: 'Registrations',
                data: registrationData,
                backgroundColor: '#6366F1',
                borderColor: '#6366F1',
                borderRadius: 4,
                borderSkipped: false,
                barPercentage: 0.7
              },
              {
                label: 'Offers',
                data: offerData,
                backgroundColor: '#10B981',
                borderColor: '#10B981',
                borderRadius: 4,
                borderSkipped: false,
                barPercentage: 0.7
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: this.institutionChartType === 'horizontal' ? 'y' as const : 'x' as const,
            ...(this.institutionChartType === 'doughnut' ? { cutout: '60%' } : {}),
            interaction: {
              mode: 'index' as const,
              intersect: false
            },
            plugins: {
              legend: {
                display: isCircular || this.institutionChartType === 'horizontal' || this.institutionChartType === 'table',
                position: 'bottom' as const,
                labels: {
                  font: { size: 11, family: 'Poppins, sans-serif' },
                  color: '#6B7280',
                  padding: 15,
                  usePointStyle: true,
                  pointStyle: 'circle'
                }
              },
              tooltip: {
                enabled: true,
                backgroundColor: '#fff',
                titleColor: '#111827',
                bodyColor: '#6b7280',
                borderColor: '#e5e7eb',
                borderWidth: 1,
                padding: 14,
                boxPadding: 8,
                usePointStyle: true,
                displayColors: false,
                titleFont: { size: 13, family: 'Poppins, sans-serif', weight: 'bold' as const },
                bodyFont: { size: 12, family: 'Poppins, sans-serif', lineHeight: 1.8 },
                boxWidth: 8,
                boxHeight: 8,
                callbacks: {
                  title: (context: any) => {
                    const index = context[0].dataIndex;
                    return this.institutionData[index].institute_name;
                  },
                  label: () => {
                    return '';
                  },
                  afterBody: (context: any) => {
                    const index = context[0].dataIndex;
                    const inst = this.institutionData[index];
                    return [
                      `Registrations: ${inst.total_registrations}`,
                      `Offers: ${inst.total_offers}`,
                      `Events: ${inst.event_count}`
                    ];
                  },
                  labelColor: (context: any) => {
                    return {
                      borderColor: '#8b5cf6',
                      backgroundColor: '#8b5cf6'
                    };
                  }
                }
              }
            },
            scales: isCircular ? undefined : {
              [this.institutionChartType === 'horizontal' ? 'x' : 'y']: {
                beginAtZero: true,
                grid: {
                  color: '#F3F4F6',
                  display: true
                },
                border: {
                  display: false
                },
                ticks: {
                  color: '#6B7280',
                  font: { size: 11, family: 'Poppins, sans-serif' }
                }
              },
              [this.institutionChartType === 'horizontal' ? 'y' : 'x']: {
                grid: {
                  display: false
                },
                border: {
                  display: false
                },
                ticks: {
                  color: '#374151',
                  font: { size: 11, family: 'Poppins, sans-serif' },
                  padding: 8,
                  autoSkip: false,
                  maxRotation: this.institutionChartType === 'horizontal' ? 0 : 45,
                  minRotation: this.institutionChartType === 'horizontal' ? 0 : 0
                }
              }
            }
          }
        };

        this.institutionChart = new Chart(ctx, config);
      }
    }, 50);
  }

  renderDailyActivityChart(): void {
    if (this.dailyActivityChart) {
      this.dailyActivityChart.destroy();
      this.dailyActivityChart = null;
    }

    if (!this.dailyActivityChartCanvas || this.isChartsLoading) return;

    const canvas = this.dailyActivityChartCanvas.nativeElement;
    const existingChart = Chart.getChart(canvas);
    if (existingChart) {
      existingChart.destroy();
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    setTimeout(() => {
      if (!Chart.getChart(canvas)) {
        const labels = this.dailyActivityData.map(d => {
          const date = new Date(d.date);
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }).reverse();
        const registrationData = this.dailyActivityData.map(d => d.registrations).reverse();
        const interviewedData = this.dailyActivityData.map(d => d.interviewed).reverse();
        const assessmentsData = this.dailyActivityData.map(d => d.assessments_taken).reverse();
        const offeredData = this.dailyActivityData.map(d => d.offered).reverse();

        const isArea = this.dailyActivityChartType === 'area';
        const isRadar = this.dailyActivityChartType === 'radar';

        let chartType: ChartType = 'bar';
        if (isRadar) chartType = 'radar';
        else if (isArea) chartType = 'line';
        else chartType = 'bar';

        const config: ChartConfiguration = {
          type: chartType,
          data: {
            labels: labels,
            datasets: [
              {
                label: 'Registrations',
                data: registrationData,
                backgroundColor: isArea ? this.createGradient(ctx, '#6366F1', true) : (isRadar ? 'rgba(99, 102, 241, 0.2)' : '#6366F1'),
                borderColor: '#6366F1',
                borderWidth: isArea || isRadar ? 3 : 0,
                tension: 0.4,
                pointRadius: isArea ? 0 : (isRadar ? 3 : 0),
                pointHoverRadius: isArea || isRadar ? 6 : 0,
                pointBackgroundColor: '#6366F1',
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                fill: isArea || isRadar,
                borderRadius: this.dailyActivityChartType === 'bar' ? 4 : 0
              },
              {
                label: 'Interviewed',
                data: interviewedData,
                backgroundColor: isArea ? this.createGradient(ctx, '#8B5CF6', true) : (isRadar ? 'rgba(139, 92, 246, 0.2)' : '#8B5CF6'),
                borderColor: '#8B5CF6',
                borderWidth: isArea || isRadar ? 3 : 0,
                tension: 0.4,
                pointRadius: isArea ? 0 : (isRadar ? 3 : 0),
                pointHoverRadius: isArea || isRadar ? 6 : 0,
                pointBackgroundColor: '#8B5CF6',
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                fill: isArea || isRadar,
                borderRadius: this.dailyActivityChartType === 'bar' ? 4 : 0
              },
              {
                label: 'Assessments',
                data: assessmentsData,
                backgroundColor: isArea ? this.createGradient(ctx, '#EC4899', true) : (isRadar ? 'rgba(236, 72, 153, 0.2)' : '#EC4899'),
                borderColor: '#EC4899',
                borderWidth: isArea || isRadar ? 3 : 0,
                tension: 0.4,
                pointRadius: isArea ? 0 : (isRadar ? 3 : 0),
                pointHoverRadius: isArea || isRadar ? 6 : 0,
                pointBackgroundColor: '#EC4899',
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                fill: isArea || isRadar,
                borderRadius: this.dailyActivityChartType === 'bar' ? 4 : 0
              },
              {
                label: 'Offered',
                data: offeredData,
                backgroundColor: isArea ? this.createGradient(ctx, '#10B981', true) : (isRadar ? 'rgba(16, 185, 129, 0.2)' : '#10B981'),
                borderColor: '#10B981',
                borderWidth: isArea || isRadar ? 3 : 0,
                tension: 0.4,
                pointRadius: isArea ? 0 : (isRadar ? 3 : 0),
                pointHoverRadius: isArea || isRadar ? 6 : 0,
                pointBackgroundColor: '#10B981',
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                fill: isArea || isRadar,
                borderRadius: this.dailyActivityChartType === 'bar' ? 4 : 0
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
              mode: 'index' as const,
              intersect: false
            },
            plugins: {
              legend: {
                display: true,
                position: 'bottom' as const,
                labels: {
                  font: { size: 11, family: 'Poppins, sans-serif' },
                  color: '#6B7280',
                  padding: 15,
                  usePointStyle: true,
                  pointStyle: 'circle'
                }
              },
              tooltip: {
                backgroundColor: '#fff',
                titleColor: '#111827',
                bodyColor: '#6b7280',
                borderColor: '#e5e7eb',
                borderWidth: 1,
                padding: 14,
                boxPadding: 8,
                usePointStyle: true,
                displayColors: false,
                titleFont: { size: 13, family: 'Poppins, sans-serif', weight: 'bold' as const },
                bodyFont: { size: 12, family: 'Poppins, sans-serif', lineHeight: 1.8 },
                boxWidth: 8,
                boxHeight: 8,
                callbacks: {
                  labelColor: () => ({
                    borderColor: '#8b5cf6',
                    backgroundColor: '#8b5cf6'
                  })
                }
              }
            },
            scales: isRadar ? {
              r: {
                beginAtZero: true,
                grid: {
                  color: '#F3F4F6'
                },
                ticks: {
                  color: '#6B7280',
                  font: { size: 10, family: 'Poppins, sans-serif' },
                  backdropColor: 'transparent'
                },
                pointLabels: {
                  color: '#374151',
                  font: { size: 11, family: 'Poppins, sans-serif' }
                }
              }
            } : {
              y: {
                beginAtZero: true,
                grid: {
                  color: '#F3F4F6',
                  display: true
                },
                border: {
                  display: false
                },
                ticks: {
                  color: '#6B7280',
                  font: { size: 11, family: 'Poppins, sans-serif' },
                  padding: 8
                }
              },
              x: {
                grid: {
                  display: false
                },
                border: {
                  display: false
                },
                ticks: {
                  color: '#374151',
                  font: { size: 11, family: 'Poppins, sans-serif' },
                  padding: 8,
                  maxRotation: 0,
                  autoSkip: true
                }
              }
            }
          }
        };

        this.dailyActivityChart = new Chart(ctx, config);
      }
    }, 50);
  }

  renderSourcePerformanceChart() {
    if (this.sourcePerformanceChart) {
      this.sourcePerformanceChart.destroy();
      this.sourcePerformanceChart = null;
    }

    if (!this.sourcePerformanceChartCanvas || this.isAnalyticsLoading) return;

    const canvas = this.sourcePerformanceChartCanvas.nativeElement;
    const existingChart = Chart.getChart(canvas);
    if (existingChart) {
      existingChart.destroy();
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    setTimeout(() => {
      if(!Chart.getChart(canvas)) {
        const sourceData = this.sourcePerformanceData;
        const labels = sourceData.map(s => {
          return this.truncateText(s.name || "Direct", 12);
        });
        const totalCandidates = sourceData.map(s => {
          return s.candidates || 0;
        })
        const offeredCandidates = sourceData.map(s => {
          return s.offered || 0;
        })
        const acceptedCandidates = sourceData.map(s => {
          return s.accepted || 0;
        })

        const isCircular = ['polarArea', 'pie', 'doughnut'].includes(this.sourcePerformanceChartType);
        const colors = ['#6366F1', '#8B5CF6', '#EC4899', '#10B981', '#F59E0B'];
        const isArea = this.sourcePerformanceChartType === "area";
        const isPie = this.sourcePerformanceChartType === "pie";
        const isDoughnut = this.sourcePerformanceChartType === "doughnut";
        let chartType: ChartType = "bar";
        if(isArea) chartType = "line";
        if(isPie) chartType = "pie";
        if(isDoughnut) chartType = "doughnut";

        const config: ChartConfiguration = {
          type: chartType,
          data: {
            labels: labels,
            datasets: isCircular ? [
              {
                label: 'Total Candidates',
                data: totalCandidates,
                backgroundColor: colors,
                borderWidth: 0
              }
            ] : [
              {
                label: "Total Candidates",
                data: totalCandidates,
                backgroundColor: isArea ? this.createGradient(ctx, '#6366F1', true) : '#6366F1',
                borderColor: '#6366F1',
                borderWidth: isArea ? 3 : 0,
                tension: 0.4,
                pointRadius: isArea ? 0 : 0,
                pointHoverRadius: isArea ? 6 : 0,
                pointBackgroundColor: '#6366F1',
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                fill: isArea,
                borderRadius: this.sourcePerformanceChartType === 'bar' ? 4 : 0
              },
              {
                label: "Offered",
                data: offeredCandidates,
                backgroundColor: isArea ? this.createGradient(ctx, '#F59E0B', true) : '#F59E0B',
                borderColor: '#F59E0B',
                borderWidth: isArea ? 3 : 0,
                tension: 0.4,
                pointRadius: isArea ? 0 : 0,
                pointHoverRadius: isArea ? 6 : 0,
                pointBackgroundColor: '#F59E0B',
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                fill: isArea,
                borderRadius: this.sourcePerformanceChartType === 'bar' ? 4 : 0
              },
              {
                label: "Accepted",
                data: acceptedCandidates,
                backgroundColor: isArea ? this.createGradient(ctx, '#14B8A6', true) : '#14B8A6',
                borderColor: '#14B8A6',
                borderWidth: isArea ? 3 : 0,
                tension: 0.4,
                pointRadius: isArea ? 0 : 0,
                pointHoverRadius: isArea ? 6 : 0,
                pointBackgroundColor: '#14B8A6',
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                fill: isArea,
                borderRadius: this.sourcePerformanceChartType === 'bar' ? 4 : 0
              },
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
              mode: 'index' as const,
              intersect: false
            },
            plugins: {
              legend: {
                display: true,
                position: 'bottom' as const,
                labels: {
                  font: { size: 11, family: 'Poppins, sans-serif' },
                  color: '#6B7280',
                  padding: 15,
                  usePointStyle: true,
                  pointStyle: 'circle'
                }
              },
              tooltip: {
                backgroundColor: '#fff',
                titleColor: '#111827',
                bodyColor: '#6b7280',
                borderColor: '#e5e7eb',
                borderWidth: 1,
                padding: 14,
                boxPadding: 8,
                usePointStyle: true,
                displayColors: false,
                titleFont: { size: 13, family: 'Poppins, sans-serif', weight: 'bold' as const },
                bodyFont: { size: 12, family: 'Poppins, sans-serif', lineHeight: 1.8 },
                boxWidth: 8,
                boxHeight: 8,
                callbacks: {
                  title: (context: any) => {
                    const index = context[0].dataIndex;
                    return sourceData[index].name || "Direct";
                  },
                  afterBody: (context: any) => {
                    const index = context[0].dataIndex;
                    const source = sourceData[index];
                    return [
                      ...(isCircular ? [
                          `Offered: ${source.offered || 0}`,
                          `Accepted: ${source.accepted || 0}`,
                      ] : []),
                      `Rejected: ${source.rejected || 0}`,
                    ];
                  },
                }
              }
            },
            scales: this.sourcePerformanceChartType === 'doughnut' || this.sourcePerformanceChartType === 'pie' ? undefined : {
              y: {
                beginAtZero: true,
                grid: {
                  color: '#F3F4F6',
                  display: true
                },
                border: {
                  display: false
                },
                ticks: {
                  color: '#6B7280',
                  font: { size: 11, family: 'Poppins, sans-serif' },
                  padding: 8,
                  stepSize: 1
                }
              },
              x: {
                grid: {
                  display: false
                },
                border: {
                  display: false
                },
                ticks: {
                  color: '#374151',
                  font: { size: 11, family: 'Poppins, sans-serif' },
                  padding: 8,
                  maxRotation: 0,
                  autoSkip: true,
                  stepSize: 1
                }
              }
            }
          }
        }

        this.sourcePerformanceChart = new Chart(ctx, config);
      }
    }, 50);
  }

  renderGenderBasedPerformanceChart() {
    if (this.genderBasedPerformanceChart) {
      this.genderBasedPerformanceChart.destroy();
      this.genderBasedPerformanceChart = null;
    }

    if (!this.genderBasedPerformanceChartCanvas || this.isAnalyticsLoading) return;

    const canvas = this.genderBasedPerformanceChartCanvas.nativeElement;
    const existingChart = Chart.getChart(canvas);
    if (existingChart) {
      existingChart.destroy();
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    setTimeout(() => {
      if(!Chart.getChart(canvas)) {
        const sourceData = this.genderBasedPerformanceData;
        const labels = sourceData.map(s => {
          return this.truncateText(s.gender || "Other", 15);
        });
        const totalCandidates = sourceData.map(s => {
          return s.candidates || 0;
        })
        const acceptedCandidates = sourceData.map(s => {
          return s.accepted || 0;
        })

        const isCircular = ['polarArea', 'pie', 'doughnut'].includes(this.genderBasedPerformanceChartType);
        const colors = ['#6366F1', '#8B5CF6', '#EC4899', '#10B981'];

        const isArea = this.genderBasedPerformanceChartType === "area";
        const isPie = this.genderBasedPerformanceChartType === "pie";
        const isDoughnut = this.genderBasedPerformanceChartType === "doughnut";
        let chartType: ChartType = "bar";
        if(isArea) chartType = "line";
        if(isPie) chartType = "pie";
        if(isDoughnut) chartType = "doughnut";

        const config: ChartConfiguration = {
          type: chartType,
          data: {
            labels: labels,
            datasets: isCircular ? [{
              label: 'Total Candidates',
              data: totalCandidates,
              backgroundColor: colors,
              borderWidth: 0
            }] :  [
              {
                label: "Total Candidates",
                data: totalCandidates,
                backgroundColor: isArea ? this.createGradient(ctx, '#6366F1', true) : '#6366F1',
                borderColor: '#6366F1',
                borderWidth: isArea ? 3 : 0,
                tension: 0.4,
                pointRadius: isArea ? 0 : 0,
                pointHoverRadius: isArea ? 6 : 0,
                pointBackgroundColor: '#6366F1',
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                fill: isArea,
                borderRadius: this.genderBasedPerformanceChartType === 'bar' ? 4 : 0
              },
              {
                label: "Accepted",
                data: acceptedCandidates,
                backgroundColor: isArea ? this.createGradient(ctx, '#14B8A6', true) : '#14B8A6',
                borderColor: '#14B8A6',
                borderWidth: isArea ? 3 : 0,
                tension: 0.4,
                pointRadius: isArea ? 0 : 0,
                pointHoverRadius: isArea ? 6 : 0,
                pointBackgroundColor: '#14B8A6',
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                fill: isArea,
                borderRadius: this.genderBasedPerformanceChartType === 'bar' ? 4 : 0
              },
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
              mode: 'index' as const,
              intersect: false
            },
            plugins: {
              legend: {
                display: true,
                position: 'bottom' as const,
                labels: {
                  font: { size: 11, family: 'Poppins, sans-serif' },
                  color: '#6B7280',
                  padding: 15,
                  usePointStyle: true,
                  pointStyle: 'circle'
                }
              },
              tooltip: {
                backgroundColor: '#fff',
                titleColor: '#111827',
                bodyColor: '#6b7280',
                borderColor: '#e5e7eb',
                borderWidth: 1,
                padding: 14,
                boxPadding: 8,
                usePointStyle: true,
                displayColors: false,
                titleFont: { size: 13, family: 'Poppins, sans-serif', weight: 'bold' as const },
                bodyFont: { size: 12, family: 'Poppins, sans-serif', lineHeight: 1.8 },
                boxWidth: 8,
                boxHeight: 8,
                callbacks: {
                  title: (context: any) => {
                    const index = context[0].dataIndex;
                    return sourceData[index].gender || "Other";
                  },
                  afterBody: isCircular ? (context: any) => {
                    const index = context[0].dataIndex;
                    const source = sourceData[index];
                    return [
                      `Accepted: ${source.accepted || 0}`,
                    ];
                  } : undefined,
                }
              }
            },
            scales: this.genderBasedPerformanceChartType === 'doughnut' || this.genderBasedPerformanceChartType === 'pie' ? undefined :  {
              y: {
                beginAtZero: true,
                grid: {
                  color: '#F3F4F6',
                  display: true
                },
                border: {
                  display: false
                },
                ticks: {
                  color: '#6B7280',
                  font: { size: 11, family: 'Poppins, sans-serif' },
                  padding: 8,
                  stepSize: 1
                }
              },
              x: {
                grid: {
                  display: false
                },
                border: {
                  display: false
                },
                ticks: {
                  color: '#374151',
                  font: { size: 11, family: 'Poppins, sans-serif' },
                  padding: 8,
                  maxRotation: 0,
                  autoSkip: true,
                  stepSize: 1,
                }
              }
            }
          }
        }

        this.genderBasedPerformanceChart = new Chart(ctx, config);
      }
    }, 50);
  }

  createGradient(ctx: CanvasRenderingContext2D, color: string, isArea: boolean): string | CanvasGradient {
    if (!isArea) return 'transparent';

    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, color + '40'); // 25% opacity at top
    gradient.addColorStop(1, color + '00'); // 0% opacity at bottom
    return gradient;
  }



  getChartConfig(type: string, data: any[]): ChartConfiguration {
    const labels = data.map(d => d.name);
    const values = data.map(d => d.value);
    const colors = data.map(d => d.fill);

    const maxValue = Math.max(...values, 1); // Ensure at least 1
    // Better scaling: for small values use fixed scale, for large use proportional
    let yAxisMax: number;
    if (maxValue <= 10) {
      yAxisMax = 10;
    } else if (maxValue <= 50) {
      yAxisMax = 50;
    } else if (maxValue <= 100) {
      yAxisMax = 100;
    } else {
      yAxisMax = Math.ceil(maxValue * 1.15 / 100) * 100;
    }

    const chartType = type;
    const commonOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 1000,
        easing: 'easeOutQuart' as const,
      },
      plugins: {
        legend: {
          display: type === 'pie' || type === 'doughnut' || type === 'polarArea',
          position: 'bottom' as const,
          labels: {
            font: { size: 11, family: 'Poppins, sans-serif' },
            color: '#6B7280',
            padding: 12,
            usePointStyle: true
          }
        },
        tooltip: {
          backgroundColor: '#fff',
          titleColor: '#111827',
          bodyColor: '#6b7280',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          padding: 12,
          boxPadding: 6,
          usePointStyle: true,
          displayColors: false,
          titleFont: { size: 13, family: 'Poppins, sans-serif', weight: 'bold' as const },
          bodyFont: { size: 12, family: 'Poppins, sans-serif' },
          boxWidth: 8,
          boxHeight: 8,
          callbacks: {
            labelColor: () => ({
              borderColor: '#8b5cf6',
              backgroundColor: '#8b5cf6'
            }),
            label: (context: any) => {
              let value: any;

              if (context.parsed !== undefined && context.parsed !== null) {
                if (typeof context.parsed === 'number') {
                  value = context.parsed;
                }
                else if (typeof context.parsed === 'object' && context.parsed !== null) {
                  if (chartType === 'horizontal' || chartType === 'pyramid') {
                    value = context.parsed.x !== undefined ? context.parsed.x : context.raw;
                  } else if ('y' in context.parsed) {
                    value = context.parsed.y;
                  }
                  else if ('r' in context.parsed) {
                    value = context.parsed.r;
                  }
                  else if ('x' in context.parsed) {
                    value = context.parsed.x;
                  }
                  else {
                    value = context.raw;
                  }
                }
                else {
                  value = context.parsed;
                }
              }
              else if (context.raw !== undefined && context.raw !== null) {
                value = context.raw;
              }
              else if (context.formattedValue !== undefined && context.formattedValue !== null) {
                value = context.formattedValue;
              }
              else {
                value = 0;
              }

              if (typeof value === 'object' && value !== null) {
                value = context.raw || 0;
              }

              const formattedValue = typeof value === 'number' ? value.toLocaleString() : String(value || '0');
              return `${context.label}: ${formattedValue}`;
            }
          }
        }
      }
    };

    switch (type) {
      case 'pyramid':
        const pyramidValues = [...values].reverse();
        const pyramidLabels = [...labels].reverse();
        const pyramidColors = [...colors].reverse();

        return {
          type: 'bar' as ChartType,
          data: {
            labels: pyramidLabels,
            datasets: [{
              label: 'Value',
              data: pyramidValues,
              backgroundColor: pyramidColors,
              borderWidth: 0,
              borderRadius: 6,
              borderSkipped: false
            }]
          },
          options: {
            ...commonOptions,
            indexAxis: 'y' as const,
            scales: {
              x: {
                beginAtZero: true,
                max: yAxisMax,
                grid: {
                  color: '#F3F4F6',
                  display: true
                },
                border: {
                  display: false
                },
                ticks: {
                  color: '#6B7280',
                  font: { size: 11, family: 'Poppins, sans-serif' }
                }
              },
              y: {
                grid: {
                  display: false
                },
                border: {
                  display: false
                },
                ticks: {
                  color: '#374151',
                  font: { size: 12, family: 'Poppins, sans-serif' }
                }
              }
            },
            plugins: {
              ...commonOptions.plugins,
              legend: { display: false }
            }
          }
        };

      case 'bar':
        return {
          type: 'bar' as ChartType,
          data: {
            labels: labels,
            datasets: [{
              label: 'Value',
              data: values,
              backgroundColor: colors,
              borderColor: colors,
              borderWidth: 0,
              borderRadius: 8,
              borderSkipped: false
            }]
          },
          options: {
            ...commonOptions,
            scales: {
              y: {
                beginAtZero: true,
                max: yAxisMax,
                grid: {
                  color: '#F3F4F6',
                  display: true
                },
                border: {
                  display: false
                },
                ticks: {
                  color: '#6B7280',
                  font: { size: 11, family: 'Poppins, sans-serif' }
                }
              },
              x: {
                grid: {
                  display: false
                },
                border: {
                  display: false
                },
                ticks: {
                  color: '#374151',
                  font: { size: 11, family: 'Poppins, sans-serif' }
                }
              }
            },
            plugins: {
              ...commonOptions.plugins,
              legend: { display: false }
            }
          }
        };

      case 'horizontal':
        return {
          type: 'bar' as ChartType,
          data: {
            labels: labels,
            datasets: [{
              label: 'Value',
              data: values,
              backgroundColor: colors,
              borderRadius: 8,
              borderSkipped: false
            }]
          },
          options: {
            ...commonOptions,
            indexAxis: 'y' as const,
            scales: {
              x: {
                beginAtZero: true,
                max: yAxisMax,
                grid: {
                  color: '#F3F4F6',
                  display: true
                },
                border: {
                  display: false
                },
                ticks: {
                  color: '#6B7280',
                  font: { size: 11, family: 'Poppins, sans-serif' }
                }
              },
              y: {
                grid: {
                  display: false
                },
                border: {
                  display: false
                },
                ticks: {
                  color: '#374151',
                  font: { size: 11, family: 'Poppins, sans-serif' }
                }
              }
            },
            plugins: {
              ...commonOptions.plugins,
              legend: { display: false }
            }
          }
        };

      case 'area':
        return {
          type: 'line' as ChartType,
          data: {
            labels: labels,
            datasets: [{
              label: 'Value',
              data: values,
              borderColor: '#6944D6',
              backgroundColor: (context: any) => {
                const ctx = context.chart.ctx;
                const gradient = ctx.createLinearGradient(0, 0, 0, 300);
                gradient.addColorStop(0, 'rgba(105, 68, 214, 0.3)');
                gradient.addColorStop(1, 'rgba(105, 68, 214, 0)');
                return gradient;
              },
              borderWidth: 2,
              tension: 0.4,
              fill: true,
              pointRadius: 0,
              pointHoverRadius: 6,
              pointBackgroundColor: '#6944D6'
            }]
          },
          options: {
            ...commonOptions,
            scales: {
              y: {
                beginAtZero: true,
                max: yAxisMax,
                grid: {
                  color: '#F3F4F6',
                  display: true
                },
                border: {
                  display: false
                },
                ticks: {
                  color: '#6B7280',
                  font: { size: 11, family: 'Poppins, sans-serif' }
                }
              },
              x: {
                grid: {
                  color: '#F3F4F6',
                  display: true
                },
                border: {
                  display: false
                },
                ticks: {
                  color: '#374151',
                  font: { size: 11, family: 'Poppins, sans-serif' }
                }
              }
            },
            plugins: {
              ...commonOptions.plugins,
              legend: { display: false }
            }
          }
        };

      case 'pie':
        return {
          type: 'pie' as ChartType,
          data: {
            labels: labels,
            datasets: [{
              data: values,
              backgroundColor: colors,
              borderColor: '#fff',
              borderWidth: 2
            }]
          },
          options: commonOptions
        };

      case 'doughnut':
        return {
          type: 'doughnut' as ChartType,
          data: {
            labels: labels,
            datasets: [{
              data: values,
              backgroundColor: colors,
              borderColor: '#fff',
              borderWidth: 2
            }]
          },
          options: {
            ...commonOptions,
            cutout: '60%'
          } as any
        };

      case 'polarArea':
        return {
          type: 'polarArea' as ChartType,
          data: {
            labels: labels,
            datasets: [{
              data: values,
              backgroundColor: colors.map(c => c + 'CC'), // Add transparency
              borderColor: '#fff',
              borderWidth: 2
            }]
          },
          options: commonOptions
        };

      case 'radar':
        return {
          type: 'radar' as ChartType,
          data: {
            labels: labels,
            datasets: [{
              label: 'Value',
              data: values,
              backgroundColor: 'rgba(105, 68, 214, 0.2)',
              borderColor: '#6944D6',
              borderWidth: 2,
              pointBackgroundColor: '#6944D6',
              pointBorderColor: '#fff',
              pointBorderWidth: 2,
              pointRadius: 4,
              pointHoverRadius: 6
            }]
          },
          options: {
            ...commonOptions,
            scales: {
              r: {
                beginAtZero: true,
                max: yAxisMax,
                grid: {
                  color: '#F3F4F6'
                },
                angleLines: {
                  color: '#F3F4F6'
                },
                pointLabels: {
                  color: '#374151',
                  font: { size: 11, family: 'Poppins, sans-serif' }
                },
                ticks: {
                  color: '#6B7280',
                  font: { size: 10, family: 'Poppins, sans-serif' },
                  backdropColor: 'transparent'
                }
              }
            },
            plugins: {
              ...commonOptions.plugins,
              legend: { display: false }
            }
          }
        };

      default:
        return this.getChartConfig('pyramid', data);
    }
  }

  formatTrend(value: number | null): string {
    if (value === null || value === undefined) return '0%';
    if (value > 0) return `+${value.toFixed(1)}%`;
    if (value < 0) return `${value.toFixed(1)}%`;
    return '0%';
  }

  getTrendClass(value: number | null): string {
    if (value === null || value === undefined) return 'trend-positive';
    if (value > 0) return 'trend-positive';
    if (value < 0) return 'trend-negative';
    return 'trend-positive';
  }

  exportToCSV(data: any[], filename: string): void {
    if (!data || data.length === 0) return;

    const filteredData = data.map(row => {
      const { fill, ...rest } = row;
      return rest;
    });

    const headers = Object.keys(filteredData[0]).join(',');
    const rows = filteredData.map(row => Object.values(row).join(','));
    const csvContent = [headers, ...rows].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  formatNumber(num: number): string {
    if (!num) return '0';
    return num.toLocaleString();
  }

  changeDailyActivityChart(type: string): void {
    this.dailyActivityChartType = type;
    this.onDailyActivityChartTypeChange();
  }

  changeRolesChart(type: string): void {
    this.rolesChartType = type;
    this.onRolesChartTypeChange();
  }

  changeFunnelChart(type: string): void {
    this.funnelChartType = type;
    this.cdr.detectChanges();
    setTimeout(() => this.onFunnelChartTypeChange(), 100);
  }

  changeInstitutionChart(type: string): void {
    this.institutionChartType = type;
    this.onInstitutionChartTypeChange();
  }

  get eventsData(): any {
    return this.summaryData || {};
  }

  get Math() {
    return Math;
  }

  getTimeFilterLabel(): string {
    const option = this.timeOptions.find(opt => opt.value === this.timeFilter);
    return option ? option.label : 'Last 90 days';
  }

  toggleChartSelector(chartKey: string): void {
    // Close all other selectors
    Object.keys(this.showChartSelector).forEach(key => {
      if (key !== chartKey) {
        this.showChartSelector[key] = false;
      }
    });
    // Toggle the current selector
    this.showChartSelector[chartKey] = !this.showChartSelector[chartKey];
  }

  closeAllChartSelectors(): void {
    Object.keys(this.showChartSelector).forEach(key => {
      this.showChartSelector[key] = false;
    });
  }

  selectChartType(chartKey: string, chartType: string): void {
    switch (chartKey) {
      case 'dailyActivity':
        this.changeDailyActivityChart(chartType);
        break;
      case 'topRoles':
        this.rolesChartType = chartType;
        this.cdr.detectChanges();
        setTimeout(() => {
          setTimeout(() => this.renderRolesChart(), 50);
        }, 100);
        break;
      case 'funnel':
        this.funnelChartType = chartType;
        this.cdr.detectChanges();
        setTimeout(() => {
          setTimeout(() => this.renderFunnelChart(), 50);
        }, 100);
        break;
      case 'dropoff':
        this.dropoffChartType = chartType;
        this.cdr.detectChanges();
        setTimeout(() => {
          setTimeout(() => this.renderDropoffChart(), 50);
        }, 100);
        break;
      case 'campusStats':
        this.institutionChartType = chartType;
        this.cdr.detectChanges();
        setTimeout(() => {
          if (chartType === 'table' || chartType === 'horizontal') {
            // No need to render chart for table or horizontal
            return;
          }
          setTimeout(() => this.renderInstitutionChart(), 50);
        }, 100);
        break;
      case 'eventType':
        this.eventTypeChartType = chartType;
        this.cdr.detectChanges();
        setTimeout(() => {
          setTimeout(() => this.renderEventTypeChart(), 50);
        }, 100);
        break;
      case 'sourcePerformance':
        this.sourcePerformanceChartType = chartType;
        this.cdr.detectChanges();
        setTimeout(() => {
          setTimeout(() => this.renderSourcePerformanceChart(), 50);
        }, 100);
        break;
      case 'genderBasedPerformance':
        this.genderBasedPerformanceChartType = chartType;
        this.cdr.detectChanges();
        setTimeout(() => {
          setTimeout(() => this.renderGenderBasedPerformanceChart(), 50);
        }, 100);
        break;
    }
    this.showChartSelector[chartKey] = false;
  }

  getCurrentChartIcon(chartKey: string): string {
    let currentType = '';
    let options: any[] = [];

    switch (chartKey) {
      case 'dailyActivity':
        currentType = this.dailyActivityChartType;
        options = this.dailyActivityChartOptions;
        break;
      case 'topRoles':
        currentType = this.rolesChartType;
        options = this.rolesChartOptions;
        break;
      case 'funnel':
        currentType = this.funnelChartType;
        options = this.chartTypeOptions;
        break;
      case 'dropoff':
        currentType = this.dropoffChartType;
        options = this.dropoffChartOptions;
        break;
      case 'campusStats':
        currentType = this.institutionChartType;
        options = this.institutionChartOptions;
        break;
      case 'eventType':
        currentType = this.eventTypeChartType;
        options = this.eventTypeChartOptions;
        break;
      case 'genderBasedPerformance':
        currentType = this.genderBasedPerformanceChartType;
        options = this.genderBasedPerformanceChartOptions;
        break;
      case 'sourcePerformance':
        currentType = this.sourcePerformanceChartType;
        options = this.sourcePerformanceChartOptions;
        break;
    }

    const option = options.find(opt => opt.value === currentType);
    return option ? option.icon : 'pi-chart-bar';
  }

  getPeakActivitySubtitle(): string {
    if (!this.dailyActivityData || this.dailyActivityData.length === 0) {
      return 'No activity data available';
    }

    // Find peak activity
    let maxValue = 0;
    let peakDate = '';
    let peakType = '';

    this.dailyActivityData.forEach(day => {
      const total = (day.registrations || 0) + (day.interviewed || 0) + (day.offered || 0);
      if (total > maxValue) {
        maxValue = total;
        peakDate = day.date;
        // Determine dominant type
        const types = [
          { name: 'registrations', value: day.registrations || 0 },
          { name: 'interviews', value: day.interviewed || 0 },
          { name: 'offers', value: day.offered || 0 }
        ];
        peakType = types.sort((a, b) => b.value - a.value)[0].name;
      }
    });

    // Format date as 'Nov 27'
    const formattedDate = this.formatDateShort(peakDate);
    return `Peak activity on ${formattedDate} with ${peakType}`;
  }

  formatDateShort(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getDate()}`;
  }

  getTopRoleSubtitle(): string {
    if (!this.topPerformingData || this.topPerformingData.length === 0) {
      return 'No role data available';
    }

    const topRole = this.topPerformingData[0];
    const roleName = topRole.job_role_name || 'N/A';
    // Capitalize first letter
    const capitalizedRole = roleName.charAt(0).toUpperCase() + roleName.slice(1).toLowerCase();
    return `${capitalizedRole} most in-demand`;
  }

  getFunnelSubtitle(): string {
    if (!this.funnelStages || this.funnelStages.length === 0) {
      return 'Funnel conversion analysis';
    }

    const applied = this.funnelStages.find(s => s.name === 'Applied')?.value || 0;

    return `${applied} candidates registered`;
  }

  getDropoffSubtitle(): string {
    if (!this.dropoffData || this.dropoffData.length === 0) {
      return 'No dropoff data available';
    }

    // Find stage with highest dropoff percentage
    let maxDropoff = 0;
    let maxStage = '';

    this.dropoffData.forEach(stage => {
      const dropoffPercent = stage.entered > 0 ? Math.round((stage.dropOff / stage.entered) * 100) : 0;
      if (dropoffPercent > maxDropoff) {
        maxDropoff = dropoffPercent;
        maxStage = stage.name;
      }
    });

    if (!maxStage || maxDropoff === 0) {
      return 'No significant dropoff detected';
    }

    return `${maxStage} has highest dropoff at ${maxDropoff}%`;
  }

  getCampusSubtitle(): string {
    if (!this.institutionData || this.institutionData.length === 0) {
      return 'Top performing institutions';
    }

    const topInstitution = this.institutionData[0];
    return `${topInstitution.institute_name || 'Top institution'} performing well`;
  }

  getInstitutionAbbreviation(fullName: string): string {
    if (!fullName) return '';

    // Remove common words: 'and', 'of', 'the'
    const words = fullName
      .toLowerCase()
      .split(/\s+/)
      .filter(word => word && !['and', 'of', 'the'].includes(word));

    // Take first letter of each word
    const abbreviation = words
      .map(word => word.charAt(0).toUpperCase())
      .join('');

    return abbreviation;
  }

  getMaxValue(): number {
    if (!this.institutionData || this.institutionData.length === 0) return 0;
    return Math.max(...this.institutionData.map(d => d.total_registrations || 0));
  }

  getEventTypeSubtitle(): string {
    if (!this.eventTypeData || this.eventTypeData.length === 0) {
      return 'Event distribution';
    }

    const leading = this.eventTypeData.sort((a, b) => (b.percentage || 0) - (a.percentage || 0))[0];
    const percentage = Math.round(leading.percentage || 0);

    return `${leading.event_type || 'N/A'} leads at ${percentage}%`;
  }
}